import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Package, Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#F9FAFB]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-[#0A0A0A] rounded-sm flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">Reco Stock</h1>
              <p className="text-sm text-[#525252]">Gestion de stock pneus</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white border border-[#E5E7EB] rounded-md shadow-sm p-6">
            <h2 className="text-xl font-semibold text-[#0A0A0A] mb-1">Connexion</h2>
            <p className="text-sm text-[#525252] mb-6">Accédez à votre espace de gestion</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div 
                  className="p-3 bg-[#FEE2E2] border border-[#FECACA] rounded-sm text-sm text-[#991B1B]"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="email" className="input-label">
                  Adresse email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@recostock.fr"
                  className="input-field"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="input-label">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-field pr-10"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#525252]"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary"
                data-testid="login-submit-button"
              >
                {isLoading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-[#A3A3A3] mt-6">
            Application interne — Accès réservé aux employés
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGluZHVzdHJpYWwlMjB3YXJlaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzQ3MDE5Mjd8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-[#0A0A0A] rounded-sm flex items-center justify-center mx-auto mb-6">
              <Package className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#0A0A0A] mb-3">Reco Stock</h2>
            <p className="text-[#525252] max-w-sm">
              Système de gestion de stock de pneus — Neufs et Occasion
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
